import { advance, dateOf, dayOf, earn, message, spend, type LifeEvent, type LifeState, money } from './life.ts';
import type { Skill } from './content/types.ts';
import { hereCountry, localPrice, nudge } from './character.ts';
import type { Country } from './travel.ts';

/**
 * Careers as ladders you climb: each job has its own rungs (a junior developer becomes a tech lead,
 * a nurse becomes a matron), and each rung asks for something: good work for long enough, real
 * skill, and sometimes a professional certificate you have to study for and pass. Promotions are a
 * moment (your manager calls you in, and you can push for more). Every month there's a review.
 */

const naira = (s: LifeState, n: number) => money(s, n);
const clamp = (v: number) => Math.max(0, Math.min(100, v));

export interface Rung { title: string; pay: number; skill?: [Skill, number]; cert?: string; shifts: number }

/** Rungs above the job you start in (the first rung is the job itself). `pay` multiplies the starting salary. */
export const LADDERS: Record<string, Rung[]> = {
  dev: [{ title: 'Senior developer', pay: 1.45, skill: ['technical', 55], shifts: 10 }, { title: 'Tech lead', pay: 1.9, skill: ['technical', 68], cert: 'cloud', shifts: 15 }, { title: 'Engineering manager', pay: 2.5, skill: ['social', 45], cert: 'pmp', shifts: 20 }],
  banker: [{ title: 'Relationship manager', pay: 1.35, skill: ['social', 45], shifts: 10 }, { title: 'Senior relationship manager', pay: 1.7, skill: ['business', 55], cert: 'ican', shifts: 15 }, { title: 'Branch manager', pay: 2.3, skill: ['business', 65], shifts: 20 }],
  accountant: [{ title: 'Senior accountant', pay: 1.35, skill: ['business', 50], cert: 'ican', shifts: 10 }, { title: 'Finance manager', pay: 1.8, skill: ['business', 62], shifts: 15 }, { title: 'Chief financial officer', pay: 2.8, skill: ['social', 50], cert: 'pmp', shifts: 23 }],
  engineer: [{ title: 'Project engineer', pay: 1.35, skill: ['technical', 52], cert: 'coren', shifts: 10 }, { title: 'Project manager', pay: 1.8, skill: ['business', 40], cert: 'pmp', shifts: 15 }, { title: 'Engineering director', pay: 2.5, skill: ['technical', 70], shifts: 23 }],
  nurse: [{ title: 'Senior nurse', pay: 1.3, skill: ['academics', 45], shifts: 10 }, { title: 'Nursing officer', pay: 1.65, skill: ['social', 45], shifts: 15 }, { title: 'Matron', pay: 2.1, skill: ['academics', 60], shifts: 20 }],
  // (the long climb, and the best paid at the top: a consultant surgeon out-earns an engineering manager)
  doctor: [{ title: 'Registrar', pay: 1.7, skill: ['academics', 55], shifts: 10 }, { title: 'Senior registrar', pay: 2.6, skill: ['academics', 65], shifts: 15 }, { title: 'Consultant surgeon', pay: 4.5, skill: ['academics', 72], cert: 'fellowship', shifts: 21 }, { title: 'Head of surgery', pay: 7.0, skill: ['social', 45], shifts: 28 }],
  teacher: [{ title: 'Senior teacher', pay: 1.3, skill: ['academics', 50], shifts: 10 }, { title: 'Vice principal', pay: 1.7, skill: ['social', 45], shifts: 15 }, { title: 'Principal', pay: 2.2, skill: ['business', 40], cert: 'pmp', shifts: 23 }],
  lawyer: [{ title: 'Associate', pay: 1.4, skill: ['academics', 55], shifts: 10 }, { title: 'Senior associate', pay: 1.85, skill: ['social', 50], shifts: 16 }, { title: 'Partner', pay: 3.0, skill: ['social', 65], shifts: 25 }],
  civil: [{ title: 'Higher executive officer', pay: 1.25, shifts: 13 }, { title: 'Principal officer', pay: 1.55, skill: ['academics', 45], shifts: 18 }, { title: 'Assistant director', pay: 2.0, cert: 'pmp', shifts: 25 }],
  chef: [{ title: 'Line chef', pay: 1.3, skill: ['cooking', 45], shifts: 9 }, { title: 'Head chef', pay: 1.8, skill: ['cooking', 62], cert: 'culinary', shifts: 15 }, { title: 'Kitchen manager', pay: 2.2, skill: ['business', 40], shifts: 20 }],
  mechanic: [{ title: 'Master mechanic', pay: 1.4, skill: ['technical', 55], shifts: 11 }, { title: 'Workshop owner', pay: 2.0, skill: ['business', 40], shifts: 18 }],
  apprentice_mechanic: [{ title: 'Mechanic', pay: 3.0, skill: ['technical', 35], shifts: 8 }, { title: 'Master mechanic', pay: 4.5, skill: ['technical', 55], shifts: 15 }],
  apprentice_tailor: [{ title: 'Tailor', pay: 3.0, skill: ['creativity', 35], shifts: 8 }, { title: 'Fashion designer', pay: 5.0, skill: ['creativity', 55], shifts: 15 }],
  shop_attendant: [{ title: 'Shift supervisor', pay: 1.3, skill: ['social', 35], shifts: 9 }, { title: 'Store manager', pay: 1.8, skill: ['business', 40], shifts: 15 }],
  waiter: [{ title: 'Head waiter', pay: 1.3, skill: ['social', 40], shifts: 9 }, { title: 'Restaurant manager', pay: 1.9, skill: ['business', 40], shifts: 15 }],
  // (training makes a candidate an astronaut; flights and years make a commander)
  astronaut: [{ title: 'Astronaut', pay: 1.2, skill: ['fitness', 45], shifts: 3 }, { title: 'Senior astronaut', pay: 1.45, skill: ['academics', 65], shifts: 15 }, { title: 'Station commander', pay: 1.8, skill: ['social', 50], shifts: 25 }],
  intern: [{ title: 'Junior associate', pay: 1.8, skill: ['academics', 40], shifts: 8 }],
  refinery: [{ title: 'Senior operator', pay: 1.35, skill: ['technical', 50], shifts: 10 }, { title: 'Shift supervisor', pay: 1.8, skill: ['technical', 62], shifts: 16 }, { title: 'Plant manager', pay: 2.6, skill: ['business', 45], cert: 'pmp', shifts: 24 }],
  firefighter: [{ title: 'Leading firefighter', pay: 1.3, skill: ['fitness', 50], shifts: 10 }, { title: 'Station officer', pay: 1.7, skill: ['social', 45], shifts: 16 }, { title: 'Fire chief', pay: 2.3, skill: ['fitness', 65], shifts: 24 }],
  pharmacist: [{ title: 'Senior pharmacist', pay: 1.3, skill: ['academics', 50], shifts: 10 }, { title: 'Pharmacy manager', pay: 1.7, skill: ['business', 40], shifts: 16 }],
  photographer: [{ title: 'Wedding photographer', pay: 1.5, skill: ['creativity', 45], shifts: 8 }, { title: 'Studio owner', pay: 2.4, skill: ['business', 40], shifts: 16 }],
};

/** Professional certificates: pay, study, sit the exam. */
export interface Cert { id: string; label: string; price: number; sessions: number; for: string }
export const CERTS: Cert[] = [
  { id: 'ican', label: 'ICAN chartered accountant (professional exams)', price: 350_000, sessions: 2, for: 'Accounting and banking' },
  { id: 'coren', label: 'COREN engineering registration', price: 250_000, sessions: 2, for: 'Engineers' },
  { id: 'cloud', label: 'Cloud architect certification', price: 300_000, sessions: 2, for: 'Software developers' },
  { id: 'pmp', label: 'PMP project management', price: 450_000, sessions: 2, for: 'Anyone heading into management' },
  { id: 'culinary', label: 'Culinary diploma', price: 200_000, sessions: 2, for: 'Cooks and chefs' },
  { id: 'mdcn', label: 'MDCN medical licence (licensing exams)', price: 180_000, sessions: 2, for: 'Medical graduates: a hospital needs it' },
  { id: 'astro', label: 'Astronaut selection exams', price: 400_000, sessions: 2, for: 'Applying to the Astronaut Corps: orbits, rockets, the body in space' },
  { id: 'fellowship', label: 'Surgical fellowship (FWACS)', price: 650_000, sessions: 3, for: 'Doctors heading for consultant' },
];

/**
 * The national qualifications go by the country's own name where you are (the same exam, in game terms):
 * ICAN and COREN are Nigerian bodies, so a life in Shanghai sees the CICPA and a registered engineer.
 */
const CERT_LOCAL: Record<string, Partial<Record<Country, [label: string, forWhom: string]>>> = {
  mdcn: {
    france: ['Épreuves dématérialisées nationales (EDN, internat)', 'Medical graduates: a hospital needs it'],
    usa: ['USMLE, the US medical licensing exams', 'Medical graduates: a hospital needs it'],
    china: ['National medical licensing exam (执业医师资格考试)', 'Medical graduates: a hospital needs it'],
    japan: ['National medical licensing exam (医師国家試験)', 'Medical graduates: a hospital needs it'],
  },
  fellowship: {
    france: ['DES de chirurgie (surgical specialty diploma)', 'Doctors heading for consultant'],
    usa: ['Board certification in surgery (ABS)', 'Doctors heading for consultant'],
    china: ['Surgical specialist certification (专科医师)', 'Doctors heading for consultant'],
    japan: ['Board-certified surgeon (外科専門医)', 'Doctors heading for consultant'],
  },
  ican: {
    france: ['Expert-comptable (DEC, professional exams)', 'Accounting and finance'],
    usa: ['CPA, Certified Public Accountant (professional exams)', 'Accounting and banking'],
    china: ['Certified public accountant, CICPA (注册会计师)', 'Accounting and banking'],
    japan: ['Certified public accountant (公認会計士)', 'Accounting and banking'],
  },
  coren: {
    france: ["Titre d'ingénieur (CTI-accredited)", 'Engineers'],
    usa: ['PE licence (Professional Engineer)', 'Engineers'],
    china: ['Registered engineer (注册工程师)', 'Engineers'],
    japan: ['Professional engineer (技術士)', 'Engineers'],
  },
};

/** A certificate as it is where you are: the local name, a local price. */
export function certOf(s: LifeState, id: string): Cert | undefined {
  const base = CERTS.find((c) => c.id === id);
  if (!base) return undefined;
  const loc = CERT_LOCAL[id]?.[hereCountry(s)];
  return { ...base, label: loc?.[0] ?? base.label, for: loc?.[1] ?? base.for, price: localPrice(s, base.price, 1000) };
}

/** The courses on offer here: the country's own qualifications first, then the international ones. */
export function certsOf(s: LifeState): Cert[] {
  const national = CERTS.filter((c) => c.id in CERT_LOCAL), rest = CERTS.filter((c) => !(c.id in CERT_LOCAL));
  return [...national, ...rest].map((c) => certOf(s, c.id)!);
}

export interface CareerState {
  certs: string[];
  /** a course you've paid for: study sessions done, and whether you're ready to sit the exam */
  course: { id: string; done: number; attempts: number } | null;
  /** your manager wants to see you (a promotion is on the table) */
  promotion: { rung: number; title: string; pay: number } | null;
  /** monthly reviews: how many "below expectations" in a row */
  below: number;
  lastReview: number;
}

export function careerOf(s: LifeState): CareerState {
  s.career ??= { certs: [], course: null, promotion: null, below: 0, lastReview: dayOf(s.minutes) };
  // (a life born a doctor already passed the licensing exams)
  if (s.card.job?.id === 'doctor' && !s.career.certs.includes('mdcn')) s.career.certs.push('mdcn');
  return s.career;
}

/** Your ladder: where you are on it, and what the next rung needs. */
export function ladderOf(s: LifeState): { rungs: Rung[]; at: number; next: Rung | null; base: string } | null {
  if (!s.job) return null;
  const rungs = LADDERS[s.job.id];
  if (!rungs) return null;
  const at = s.job.rank ?? 0;
  return { rungs, at, next: rungs[at] ?? null, base: s.job.baseLabel ?? s.job.label };
}

/** What still stands between you and the next rung (empty = you're ready). */
export function missingFor(s: LifeState): string[] {
  const l = ladderOf(s);
  if (!l || !l.next) return [];
  const r = l.next, out: string[] = [];
  const shifts = s.work.worked - (s.job!.promotedAt ?? 0);
  if (shifts < r.shifts) out.push(`${r.shifts - shifts} more shifts`);
  if (s.work.performance < 72) out.push(`performance 80+ (you're at ${Math.round(s.work.performance)})`);
  if (r.skill && s.skills[r.skill[0]] < r.skill[1]) out.push(`${r.skill[0]} ${r.skill[1]} (you're at ${Math.round(s.skills[r.skill[0]])})`);
  if (r.cert && !careerOf(s).certs.includes(r.cert)) out.push(`the ${certOf(s, r.cert)?.label ?? r.cert}`);
  return out;
}

/** After a shift: is your manager about to call you in? (generic Senior/Lead/Head for jobs without a ladder) */
export function checkPromotion(s: LifeState): boolean {
  if (!s.job) return false;
  const c = careerOf(s);
  if (c.promotion) return true;
  const l = ladderOf(s);
  if (l) {
    if (!l.next || missingFor(s).length) return false;
    c.promotion = { rung: l.at + 1, title: l.next.title, pay: Math.round((s.job.incomeMonthly / (l.at === 0 ? 1 : l.rungs[l.at - 1].pay)) * l.next.pay / 1000) * 1000 };
    return true;
  }
  const rank = s.job.rank ?? 0;
  if (rank < 3 && s.work.performance >= 72 && s.work.worked - (s.job.promotedAt ?? 0) >= 9 + rank * 5) {
    const base = s.job.baseLabel ?? s.job.label;
    c.promotion = { rung: rank + 1, title: `${['Senior', 'Lead', 'Head'][rank]} ${base.toLowerCase()}`, pay: Math.round(s.job.incomeMonthly * 1.28 / 1000) * 1000 };
    return true;
  }
  return false;
}

/**
 * In your manager's office. Accept what's offered, or push for more: a confident, skilled ask gets
 * more; a clumsy one sours it (you still get the title, at the first number, and they remember).
 */
export function answerPromotion(s: LifeState, negotiate: boolean): { line: string; events: LifeEvent[] } {
  const c = careerOf(s);
  const p = c.promotion;
  if (!p || !s.job) return { line: '', events: [] };
  c.promotion = null;
  let pay = p.pay;
  let line: string;
  if (negotiate) {
    const chance = 0.25 + s.skills.social / 160 + (s.work.performance > 90 ? 0.15 : 0);
    const roll = ((s.seed ^ s.minutes) >>> 0) % 1000 / 1000;
    if (roll < chance) { pay = Math.round(pay * 1.12 / 1000) * 1000; line = `You make your case: the projects, the late nights, what the market pays. She sighs, then smiles. "Fine. ${naira(s, pay)}."`; nudge(s, 'ambitious', 0.04); }
    else { s.work.performance = clamp(s.work.performance - 8); line = `"I'm offering you a promotion and you're haggling?" She doesn't move on the number. ${naira(s, pay)}, and a slightly colder office.`; }
  } else line = `"Congratulations. You've earned it." ${naira(s, pay)} a month from now on.`;
  const base = s.job.baseLabel ?? s.job.label;
  s.job = { ...s.job, rank: p.rung, baseLabel: base, label: p.title, incomeMonthly: pay, promotedAt: s.work.worked };
  s.work.performance = Math.max(60, s.work.performance - 15);
  s.history.push({ t: s.minutes, text: `Promoted to ${p.title.toLowerCase()}.` });
  return { line, events: [{ t: s.minutes, kind: 'pay', text: `Promoted: ${p.title}` }] };
}

/** Once a month (on the 28th): a review. Exceeds → a bonus; below twice in a row → let go. */
export function reviewDay(s: LifeState, events: LifeEvent[]) {
  if (!s.job || dateOf(s.minutes).getUTCDate() !== 28) return;
  const c = careerOf(s);
  if (dayOf(s.minutes) - c.lastReview < 20) return;
  c.lastReview = dayOf(s.minutes);
  const p = s.work.performance;
  if (p >= 85) {
    const bonus = Math.round(s.job.incomeMonthly * 0.15 / 1000) * 1000;
    earn(s, bonus, 'Performance bonus');
    c.below = 0;
    message(s, 'Your manager', `Monthly review: exceeds expectations. ${naira(s, bonus)} bonus this month. Keep it up.`, events);
  } else if (p >= 55) {
    c.below = 0;
    message(s, 'Your manager', `Monthly review: meets expectations.${missingFor(s).length && ladderOf(s)?.next ? ` For ${ladderOf(s)!.next!.title.toLowerCase()} you'd still need: ${missingFor(s).join(', ')}.` : ''}`, events);
  } else {
    c.below++;
    if (c.below >= 2) {
      events.push({ t: s.minutes, kind: 'fired', text: `Second review below expectations. You've been let go from ${s.job.label.toLowerCase()}.` });
      s.history.push({ t: s.minutes, text: `Let go as ${s.job.label.toLowerCase()} after two poor reviews.` });
      s.job = null; s.work.owed = 0; c.below = 0;
    } else message(s, 'Your manager', 'Monthly review: below expectations. This is a formal warning. Another month like this and we\'ll have to let you go.', events);
  }
}

// --- theatre days: a doctor's special shift ---------------------------------------------------------------

/** A doctor's theatre day: every fourth shift (the second one first) you scrub in for an operation and close; the rest are the wards. */
export const theatreDay = (s: LifeState) => s.job?.id === 'doctor' && s.work.worked % 4 === 1;

/** After closing in theatre: counted, and the first is remembered. `performance` 0..1 is the stitching. */
export function afterSurgery(s: LifeState, performance: number): string {
  s.surgeries = (s.surgeries ?? 0) + 1;
  if (s.surgeries === 1) s.history.push({ t: s.minutes, text: 'Scrubbed in for your first operation.' });
  s.skills.academics = clamp(s.skills.academics + 0.4 + performance * 0.8);
  nudge(s, 'disciplined', 0.01);
  return performance > 0.8 ? 'The consultant looks at your line and nods: "Neat. You can close on my list any day."'
    : performance > 0.55 ? 'The consultant redoes one knot, then says it will heal well.'
    : 'The consultant quietly redoes two of yours. The patient is fine; your pride, less so.';
}

// --- certificates ------------------------------------------------------------------------------------

export function enroll(s: LifeState, id: string): string | null {
  const c = careerOf(s), cert = certOf(s, id);
  if (!cert) return 'No such course.';
  if (c.certs.includes(id)) return 'You already have it.';
  if (c.course) return 'Finish your current course first.';
  if (!spend(s, cert.price, cert.label)) return 'You can\'t afford the fees.';
  c.course = { id, done: 0, attempts: 0 };
  s.history.push({ t: s.minutes, text: `Enrolled for ${cert.label}.` });
  return null;
}

/** An evening's study (the game plays it as a short quiz). `quality` 0..1. */
export function studySession(s: LifeState, quality: number): LifeEvent[] {
  const c = careerOf(s);
  if (!c.course) return [];
  c.course.done = Math.min(99, c.course.done + (quality >= 0.5 ? 1 : 0.5));
  s.skills.academics = Math.min(100, s.skills.academics + 0.6 + quality);
  s.needs.energy = clamp(s.needs.energy - 10); s.needs.fun = clamp(s.needs.fun - 6);
  nudge(s, 'disciplined', 0.01);
  return advance(s, 90);
}

export const examReady = (s: LifeState) => { const c = careerOf(s); const cert = c.course && certOf(s, c.course.id); return !!cert && c.course!.done >= cert.sessions; };

/** The exam: pass and the certificate is yours; fail and you can resit (for a fee). */
export function sitExam(s: LifeState, score: number): { passed: boolean; line: string; events: LifeEvent[] } {
  const c = careerOf(s);
  const cert = c.course && certOf(s, c.course.id);
  if (!cert || !c.course) return { passed: false, line: '', events: [] };
  c.course.attempts++;
  const passed = score >= 0.6;
  if (passed) {
    c.certs.push(cert.id);
    c.course = null;
    s.history.push({ t: s.minutes, text: `Passed: ${cert.label}.` });
    nudge(s, 'ambitious', 0.03);
  } else {
    c.course.done = Math.max(0, c.course.done - 1); // one more session before a resit
    spend(s, Math.min(s.money, Math.round(cert.price * 0.2)), 'Exam resit fee');
  }
  return { passed, line: passed ? `You passed. ${cert.label}: it goes on your CV today.` : `You didn't make the pass mark (${Math.round(score * 100)}%, you needed 60%). One more study session and you can resit, for a fee.`, events: advance(s, 180) };
}

